import type { AppRole, PermissionKey } from "@livingsyncs/types";

/**
 * Scope at which a permission is meaningful — purely descriptive catalog
 * metadata (US-044 AC4). NOT consumed by enforcement in this story; a CI test
 * asserts every Permission declares it so it cannot silently rot. Wiring scope
 * into enforcement belongs with the backend companion (US-045).
 */
export type PermissionScope = "platform" | "org" | "facility";

export interface PermissionConfig {
  // Typed to the canonical PermissionKey union (US-044) so a registry key that
  // is not in PERMISSION_KEYS is a *compile* error, not only a CI failure. The
  // CI parity test (registry keys === PERMISSION_KEYS) still guards the reverse
  // direction (a key in the union with no Permission object). Cycle-free:
  // @livingsyncs/permissions already depends on @livingsyncs/types.
  key: PermissionKey;
  label: string;
  category: string;
  /** Descriptive only (US-044 AC4) — see PermissionScope. */
  scope: PermissionScope;
  /** Flags PHI-touching permissions for future audit tooling. Descriptive only (US-044 AC4). */
  phi: boolean;
  defaults?: Partial<Record<AppRole, boolean>>;
}

export class Permission {
  readonly key: PermissionKey;
  readonly label: string;
  readonly category: string;
  readonly scope: PermissionScope;
  readonly phi: boolean;
  readonly defaults: Partial<Record<AppRole, boolean>>;

  constructor(config: PermissionConfig) {
    this.key = config.key;
    this.label = config.label;
    this.category = config.category;
    this.scope = config.scope;
    this.phi = config.phi;
    this.defaults = config.defaults ?? {};
  }
}
